// hooks/useRegisterDevice.ts
import { useCallback } from 'react';
import { useRegisterDeviceMutation } from 'generated/graphql';
import { getFcmTokenFromSW } from 'serviceWorker';
import { getMessaging } from 'services/firebase';

function base64UrlToUint8(b64: string): Uint8Array {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useRegisterDevice() {
  const [register] = useRegisterDeviceMutation();
  const hasNotification = typeof Notification !== 'undefined';
  /* ---- feature-detection veloce, tutta sync ------------------------- */
  // const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const isSupported =
    'Notification' in window &&
    'serviceWorker' in navigator && // requisito di base
    window.isSecureContext;

  /* ---- handler da chiamare in un click -------------------------------- */
  const enablePush = useCallback(async () => {
    if (!isSupported) return;

    /* 1. Ottieni il permesso PRIMA di interrogare pushManager */
    const permission =
      Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
    if (permission !== 'granted') return;

    /* 2. Service-worker pronto e in controllo */
    const reg = await navigator.serviceWorker.ready;
    if (!reg.pushManager) {
      console.info(
        '[push] Push API non disponibile (iOS senza PWA o flag disabilitato)',
      );
      return;
    }

    /* 3. Chrome / Edge / Firefox via FCM */
    const messaging = await getMessaging(); // se usi Firebase
    if (messaging) {
      const token = await getFcmTokenFromSW();
      if (token) {
        await register({ variables: { input: { kind: 'fcm', token } } });
        console.log('[push] FCM token salvato');
      }
      return;
    }

    /* 4. Safari & co. via Web Push */
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8(
        process.env.VITE_VAPID_PUBLIC_KEY!,
      ),
    });

    await register({
      variables: {
        input: { kind: 'webpush', subscription: JSON.stringify(sub) },
      },
    });
    console.log('[push] Web Push subscription salvata');
  }, [isSupported, register]);

  return {
    enablePush,
    isSupported,
    isAlreadyGranted: hasNotification && Notification.permission === 'granted',
  };
}
